import { ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'

import { DataTable } from '@/components/ui'
import type { CategoryStat, Transaction } from '@/modules/projects'
import { cn, formatNumber } from '@/utils'
import { Section } from '../../shared'
import { AnomaliesSummary } from '../AnomaliesSummary'
import { categoryStatsColumns } from '../columns'
import { CategoryDetailModal } from './CategoryDetailModal'
import {
  groupOutliersByCategory,
  matchOutlierTransactions,
  outlierCategoryKey,
  toCategoryRows,
} from './utils'

interface CategoryOutliersSectionProps {
  categoryStats: CategoryStat[]
  outliers: Transaction[]
  transactions: Transaction[]
}

export function CategoryOutliersSection({
  categoryStats,
  outliers,
  transactions,
}: CategoryOutliersSectionProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [showEmptyCategories, setShowEmptyCategories] = useState(false)
  const categoriesWithOutliers = new Set(outliers.map(outlierCategoryKey)).size
  const outliersByCategory = groupOutliersByCategory(outliers)
  const allRows = toCategoryRows(categoryStats, outliersByCategory)

  const emptyCategoryCount = allRows.filter(
    (row) => row.outlierCount === 0,
  ).length

  const visibleRows = showEmptyCategories
    ? allRows
    : allRows.filter((row) => row.outlierCount > 0)

  const selectedStat = selectedCategory
    ? (categoryStats.find((stat) => stat.category === selectedCategory) ?? null)
    : null
  const selectedOutliers = selectedCategory
    ? (outliersByCategory.get(selectedCategory) ?? [])
    : []
  const selectedOutlierAmount = selectedOutliers.reduce(
    (sum, outlier) => sum + outlier.amount,
    0,
  )
  const selectedTransactions = matchOutlierTransactions(
    selectedOutliers,
    transactions,
  )

  return (
    <Section
      title="Outliers"
      description="Transactions that fall outside the typical per-category range, identified via log-IQR analysis. Click a category to open its outlier transactions and full stats."
      count={outliers.length}
    >
      <AnomaliesSummary
        totalTransactions={transactions.length}
        categoryCount={categoryStats.length}
        categoriesWithOutliers={categoriesWithOutliers}
        outlierCount={outliers.length}
      />
      <DataTable
        columns={categoryStatsColumns}
        data={visibleRows}
        height="h-auto"
        enableRowSelection={false}
        onRowClick={(row) => {
          setSelectedCategory(row.original.category)
        }}
        emptyMessage="No categories with outliers."
      />
      {emptyCategoryCount > 0 && (
        <button
          type="button"
          onClick={() => setShowEmptyCategories((current) => !current)}
          className="flex w-full cursor-pointer items-center justify-between border-t px-6 py-3 text-left text-muted-foreground text-sm transition-colors hover:bg-blue-1 hover:text-foreground"
        >
          <span>
            {showEmptyCategories
              ? `Hide ${formatNumber(emptyCategoryCount)} categories with no outliers`
              : `Show ${formatNumber(emptyCategoryCount)} categories with no outliers`}
          </span>
          <ChevronDownIcon
            className={cn(
              'size-4 transition-transform duration-200',
              showEmptyCategories && 'rotate-180',
            )}
          />
        </button>
      )}

      <CategoryDetailModal
        stat={selectedStat}
        outlierTransactions={selectedTransactions}
        outlierCount={selectedOutliers.length}
        outlierAmount={selectedOutlierAmount}
        onOpenChange={(open) => {
          if (!open) setSelectedCategory(null)
        }}
      />
    </Section>
  )
}
